import React, { useState, useCallback } from 'react';
import {
  Panel,
  PanelType,
  PrimaryButton,
  DefaultButton,
  TextField,
  Dropdown,
  IDropdownOption,
  MessageBar,
  MessageBarType,
  ProgressIndicator,
  Icon,
} from '@fluentui/react';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import { Customer, getAllActiveCustomers } from '@/api/customers';
import { uploadFile, UploadFileRequest } from '@/api/files';

interface FileUploadPanelProps {
  isOpen: boolean;
  onDismiss: () => void;
  onUploadSuccess?: () => void;
}

interface UploadFormValues {
  customerId: number | null;
  externalFileId: string;
  file: File | null;
}

const validationSchema = Yup.object({
  customerId: Yup.number().required('Customer is required').nullable(),
  externalFileId: Yup.string().trim(),
  file: Yup.mixed().required('File is required'),
});

const FileUploadPanel: React.FC<FileUploadPanelProps> = ({
  isOpen,
  onDismiss,
  onUploadSuccess,
}) => {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loadingCustomers, setLoadingCustomers] = useState<boolean>(false);
  const [uploading, setUploading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [dragActive, setDragActive] = useState<boolean>(false);

  React.useEffect(() => {
    if (isOpen) {
      loadCustomers();
    }
  }, [isOpen]);

  const loadCustomers = async () => {
    setLoadingCustomers(true);
    try {
      const customerData = await getAllActiveCustomers();
      setCustomers(customerData || []);
    } catch (error) {
      console.error('Failed to load customers:', error);
      setErrorMessage('Failed to load customers');
    } finally {
      setLoadingCustomers(false);
    }
  };

  const handleUpload = async (values: UploadFormValues, { resetForm }: { resetForm: () => void }) => {
    if (!values.file || !values.customerId) {
      setErrorMessage('Please select a file and customer');
      return;
    }

    setUploading(true);
    setErrorMessage('');

    try {
      const request: UploadFileRequest = {
        customerId: values.customerId,
        externalFileId: values.externalFileId.trim() || undefined,
        file: values.file,
      };

      await uploadFile(request);
      resetForm();
      if (onUploadSuccess) {
        onUploadSuccess();
      }
      onDismiss();
    } catch (error) {
      console.error('Failed to upload file:', error);
      setErrorMessage('Failed to upload file');
    } finally {
      setUploading(false);
    }
  };

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback((e: React.DragEvent, setFieldValue: (field: string, value: any) => void) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      setFieldValue('file', files[0]);
    }
  }, []);

  const customerOptions: IDropdownOption[] = customers.map((customer) => ({
    key: customer.Id,
    text: `${customer.Name} (${customer.CustomerType})`,
  }));

  return (
    <Panel
      headerText="Upload File"
      isOpen={isOpen}
      onDismiss={onDismiss}
      type={PanelType.medium}
      closeButtonAriaLabel="Close upload panel"
      isLightDismiss={!uploading}
    >
      <div style={{ padding: '20px' }}>
        {errorMessage && (
          <MessageBar
            messageBarType={MessageBarType.error}
            onDismiss={() => setErrorMessage('')}
            style={{ marginBottom: '16px' }}
          >
            {errorMessage}
          </MessageBar>
        )}

        <Formik
          initialValues={{
            customerId: null,
            externalFileId: '',
            file: null,
          }}
          validationSchema={validationSchema}
          onSubmit={handleUpload}
        >
          {({ values, errors, touched, setFieldValue, handleChange, handleBlur, isValid, dirty }) => (
            <Form>
              <Field name="customerId">
                {() => (
                  <Dropdown
                    label="Customer"
                    placeholder="Select a customer"
                    options={customerOptions}
                    selectedKey={values.customerId}
                    onChange={(_, option) => setFieldValue('customerId', option?.key)}
                    disabled={loadingCustomers || uploading}
                    required
                    errorMessage={touched.customerId && errors.customerId ? errors.customerId : undefined}
                    styles={{ root: { marginBottom: '16px' } }}
                  />
                )}
              </Field>

              <Field name="externalFileId">
                {() => (
                  <TextField
                    label="External File ID (Optional)"
                    name="externalFileId"
                    value={values.externalFileId}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder="Enter external file ID"
                    disabled={uploading}
                    errorMessage={touched.externalFileId && errors.externalFileId ? errors.externalFileId : undefined}
                    styles={{ root: { marginBottom: '20px' } }}
                  />
                )}
              </Field>

              <Field name="file">
                {() => (
                  <div style={{ marginBottom: '20px' }}>
                    <label style={{ fontWeight: '600', fontSize: '14px', marginBottom: '8px', display: 'block' }}>
                      File *
                    </label>
                    <div
                      style={{
                        border: `2px dashed ${dragActive ? '#0078d4' : touched.file && errors.file ? '#d13438' : '#c8c6c4'}`,
                        borderRadius: '4px',
                        padding: '40px 20px',
                        textAlign: 'center',
                        backgroundColor: dragActive ? '#f8f9fa' : '#ffffff',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                      }}
                      onDragEnter={handleDragEnter}
                      onDragLeave={handleDragLeave}
                      onDragOver={handleDragOver}
                      onDrop={(e) => handleDrop(e, setFieldValue)}
                      onClick={() => {
                        const input = document.createElement('input');
                        input.type = 'file';
                        input.onchange = (e) => {
                          const file = (e.target as HTMLInputElement).files?.[0];
                          if (file) {
                            setFieldValue('file', file);
                          }
                        };
                        input.click();
                      }}
                    >
                      <Icon
                        iconName={dragActive ? 'CloudAdd' : 'CloudUpload'}
                        style={{
                          fontSize: '48px',
                          color: dragActive ? '#0078d4' : '#605e5c',
                          marginBottom: '12px',
                        }}
                      />
                      <div style={{ fontSize: '16px', marginBottom: '8px', color: '#323130' }}>
                        {dragActive ? 'Drop file here' : values.file ? values.file.name : 'Drag and drop a file here'}
                      </div>
                      <div style={{ fontSize: '14px', color: '#605e5c' }}>
                        {!values.file && 'or click to browse'}
                      </div>
                      {values.file && (
                        <div style={{ fontSize: '12px', color: '#605e5c', marginTop: '8px' }}>
                          Size: {(values.file.size / 1024).toFixed(1)} KB
                        </div>
                      )}
                    </div>
                    {touched.file && errors.file && (
                      <div style={{ color: '#d13438', fontSize: '12px', marginTop: '4px' }}>
                        {errors.file}
                      </div>
                    )}
                  </div>
                )}
              </Field>

              {uploading && (
                <div style={{ marginBottom: '20px' }}>
                  <ProgressIndicator label="Uploading file..." />
                </div>
              )}

              <div style={{ display: 'flex', gap: '8px', marginTop: '20px' }}>
                <PrimaryButton
                  text="Upload"
                  type="submit"
                  disabled={uploading || !isValid || !dirty}
                  styles={{
                    root: {
                      minHeight: '40px',
                      padding: '8px 20px',
                    },
                  }}
                />
                <DefaultButton
                  text="Cancel"
                  onClick={onDismiss}
                  disabled={uploading}
                  styles={{
                    root: {
                      minHeight: '40px',
                      padding: '8px 20px',
                    },
                  }}
                />
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </Panel>
  );
};

export default FileUploadPanel;
